import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBuildingStore,
  IconCloudUpload,
  IconPlus,
  IconRefresh,
  IconTrash,
  IconUnlink,
} from '@tabler/icons-react';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';

const SYNC_COLOR: Record<string, string> = {
  synced: 'green',
  pending: 'yellow',
  error: 'red',
};

const emptyProduct = {
  name: '',
  price: '',
  currency: 'USD',
  availability: 'in stock' as 'in stock' | 'out of stock',
  imageUrl: '',
  url: '',
  description: '',
  brand: '',
};

function CatalogPage() {
  const utils = trpc.useUtils();
  const features = trpc.settings.features.list.useQuery();
  const catalogQ = trpc.catalog.get.useQuery();
  const catalog = catalogQ.data ?? null;
  const enabled = (features.data ?? []).find((f) => f.key === 'feature.meta_catalog')?.enabled;

  const products = trpc.catalog.products.list.useQuery(undefined, { enabled: !!catalog });
  const settings = trpc.catalog.commerceSettings.get.useQuery(undefined, {
    enabled: !!catalog && enabled !== false,
  });
  const connStatus = trpc.catalog.connectionStatus.useQuery(undefined, {
    enabled: enabled !== false,
  });
  const availableCatalogs = trpc.catalog.listAvailable.useQuery(undefined, {
    enabled: !catalog && enabled !== false && connStatus.data?.status === 'ok',
  });

  const [name, setName] = useState('');
  const [connectId, setConnectId] = useState('');
  const [productModal, productModalCtl] = useDisclosure(false);
  const [form, setForm] = useState(emptyProduct);

  const err = (e: { message: string }) => notifications.show({ message: e.message, color: 'red' });
  const refetchCatalog = () => {
    void utils.catalog.get.invalidate();
    void utils.catalog.products.list.invalidate();
    void utils.catalog.commerceSettings.invalidate();
  };

  const create = trpc.catalog.create.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Catalog created and linked', color: 'green' });
      setName('');
      refetchCatalog();
    },
    onError: err,
  });
  const connect = trpc.catalog.connect.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Catalog connected', color: 'green' });
      setConnectId('');
      refetchCatalog();
    },
    onError: err,
  });
  const unlink = trpc.catalog.unlink.useMutation({ onSuccess: refetchCatalog, onError: err });
  const syncFromStore = trpc.catalog.syncFromStore.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Imported ${r.imported}${r.removed ? `, removed ${r.removed} stale` : ''} products from store`,
        color: 'green',
      });
      void utils.catalog.products.list.invalidate();
    },
    onError: err,
  });
  const syncToMeta = trpc.catalog.syncToMeta.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Pushed ${r.pushed} products to Meta${r.failed ? `, ${r.failed} failed` : ''}`,
        color: r.failed ? 'orange' : 'green',
      });
      void utils.catalog.products.list.invalidate();
    },
    onError: err,
  });
  const addProduct = trpc.catalog.products.add.useMutation({
    onSuccess: () => {
      productModalCtl.close();
      setForm(emptyProduct);
      void utils.catalog.products.list.invalidate();
    },
    onError: err,
  });
  const deleteProduct = trpc.catalog.products.delete.useMutation({
    onSuccess: () => void utils.catalog.products.list.invalidate(),
    onError: err,
  });
  const setSettings = trpc.catalog.commerceSettings.set.useMutation({
    onSuccess: () => void utils.catalog.commerceSettings.invalidate(),
    onError: err,
  });

  if (catalogQ.isPending) return <Skeleton height={400} />;

  return (
    <Stack gap="md">
      <div>
        <Title order={2}>Product catalog</Title>
        <Text c="dimmed" size="sm">
          Manage the Meta Commerce catalog behind your WhatsApp catalog and product messages.
        </Text>
      </div>

      {enabled === false && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Feature not enabled">
          This feature is managed by your provider. Once enabled for your account, catalog products
          use your existing WhatsApp connection — no separate token or webhook is required.
        </Alert>
      )}

      {enabled !== false &&
        connStatus.data &&
        connStatus.data.status !== 'ok' &&
        connStatus.data.status !== 'not_enabled' && (
          <Alert color="orange" icon={<IconAlertCircle size={16} />} title="Reconnect required">
            {connStatus.data.status === 'not_connected'
              ? 'Connect your WhatsApp account to use the product catalog.'
              : 'Your WhatsApp connection needs to be reconnected to grant catalog access. No token entry is needed — just re-run the connection.'}
            <div style={{ marginTop: 8 }}>
              <Button size="xs" variant="light" component="a" href="/vendor/integrations/meta">
                Reconnect WhatsApp
              </Button>
            </div>
          </Alert>
        )}

      {!catalog ? (
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Card withBorder padding="lg">
            <Stack gap="sm">
              <Group gap="xs">
                <IconBuildingStore size={18} />
                <Text fw={600}>Create a new catalog</Text>
              </Group>
              <Text size="sm" c="dimmed">
                LeadSync creates a catalog under your Meta Business and links it to your WhatsApp
                account.
              </Text>
              <TextInput
                label="Catalog name"
                placeholder="My store"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <Button
                disabled={!name.trim()}
                loading={create.isPending}
                onClick={() => create.mutate({ name: name.trim() })}
              >
                Create catalog
              </Button>
            </Stack>
          </Card>
          <Card withBorder padding="lg">
            <Stack gap="sm">
              <Group gap="xs">
                <IconPlus size={18} />
                <Text fw={600}>Connect an existing catalog</Text>
              </Group>
              <Text size="sm" c="dimmed">
                Already have a catalog in Commerce Manager? Pick it from your Meta Business.
              </Text>
              {(availableCatalogs.data?.length ?? 0) > 0 ? (
                <Select
                  label="Choose a catalog"
                  placeholder="Select one of your Meta catalogs"
                  searchable
                  data={(availableCatalogs.data ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.name} · ${c.productCount} products`,
                  }))}
                  value={connectId || null}
                  onChange={(val) => setConnectId(val ?? '')}
                />
              ) : (
                <TextInput
                  label="Catalog id"
                  placeholder="1234567890"
                  value={connectId}
                  onChange={(e) => setConnectId(e.currentTarget.value)}
                />
              )}
              <Button
                variant="light"
                disabled={!connectId.trim()}
                loading={connect.isPending}
                onClick={() => connect.mutate({ catalogId: connectId.trim() })}
              >
                Connect catalog
              </Button>
            </Stack>
          </Card>
        </SimpleGrid>
      ) : (
        <>
          <Card withBorder padding="md">
            <Group justify="space-between" wrap="nowrap">
              <Stack gap={4}>
                <Group gap="xs">
                  <Text fw={600}>{catalog.name}</Text>
                  <Badge variant="light">{catalog.source}</Badge>
                  <Badge variant="light" color={catalog.linkStatus === 'linked' ? 'green' : 'red'}>
                    {catalog.linkStatus === 'linked' ? 'Linked to WhatsApp' : 'Link error'}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  Catalog id {catalog.catalogId} · {catalog.productCount} products
                  {catalog.lastSyncAt
                    ? ` · last sync ${new Date(catalog.lastSyncAt).toLocaleString()}`
                    : ''}
                </Text>
              </Stack>
              <Button
                variant="subtle"
                color="red"
                leftSection={<IconUnlink size={14} />}
                loading={unlink.isPending}
                onClick={() => unlink.mutate()}
              >
                Disconnect
              </Button>
            </Group>
          </Card>

          <Card withBorder padding="md">
            <Title order={5} mb="sm">
              Commerce settings
            </Title>
            {settings.isPending ? (
              <Skeleton height={60} />
            ) : (
              <Stack gap="sm">
                <Switch
                  label="Show catalog in WhatsApp chats"
                  checked={settings.data?.isCatalogVisible ?? false}
                  disabled={setSettings.isPending}
                  onChange={(e) =>
                    setSettings.mutate({ isCatalogVisible: e.currentTarget.checked })
                  }
                />
                <Switch
                  label="Enable cart"
                  checked={settings.data?.isCartEnabled ?? true}
                  disabled={setSettings.isPending}
                  onChange={(e) => setSettings.mutate({ isCartEnabled: e.currentTarget.checked })}
                />
              </Stack>
            )}
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between" mb="sm">
              <Title order={5}>Products</Title>
              <Group>
                <Button
                  variant="default"
                  size="xs"
                  leftSection={<IconRefresh size={14} />}
                  loading={syncFromStore.isPending}
                  onClick={() => syncFromStore.mutate()}
                >
                  Sync from store
                </Button>
                <Button
                  variant="default"
                  size="xs"
                  leftSection={<IconCloudUpload size={14} />}
                  loading={syncToMeta.isPending}
                  onClick={() => syncToMeta.mutate()}
                >
                  Push to Meta
                </Button>
                <Button
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={productModalCtl.open}
                >
                  Add product
                </Button>
              </Group>
            </Group>

            {products.isPending ? (
              <Skeleton height={120} />
            ) : (products.data ?? []).length === 0 ? (
              <Text size="sm" c="dimmed">
                No products yet. Sync from your store or add one manually.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={640}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Price</Table.Th>
                      <Table.Th>Availability</Table.Th>
                      <Table.Th>Source</Table.Th>
                      <Table.Th>Sync</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(products.data ?? []).map((p) => (
                      <Table.Tr key={p.id}>
                        <Table.Td>{p.name}</Table.Td>
                        <Table.Td>
                          {p.price} {p.currency}
                        </Table.Td>
                        <Table.Td>{p.availability}</Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">
                            {p.source}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label={p.syncError ?? p.syncStatus} disabled={!p.syncError}>
                            <Badge
                              variant="light"
                              size="sm"
                              color={SYNC_COLOR[p.syncStatus] ?? 'gray'}
                            >
                              {p.syncStatus}
                            </Badge>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => deleteProduct.mutate({ id: p.id })}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Card>
        </>
      )}

      <Modal opened={productModal} onClose={productModalCtl.close} title="Add product" centered>
        <Stack gap="sm">
          <TextInput
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
          />
          <Group grow>
            <TextInput
              label="Price"
              placeholder="9.99"
              required
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.currentTarget.value })}
            />
            <TextInput
              label="Currency"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.currentTarget.value.toUpperCase() })}
            />
          </Group>
          <Select
            label="Availability"
            data={['in stock', 'out of stock']}
            value={form.availability}
            onChange={(v) =>
              setForm({ ...form, availability: (v as typeof form.availability) ?? 'in stock' })
            }
          />
          <TextInput
            label="Image URL"
            value={form.imageUrl}
            onChange={(e) => setForm({ ...form, imageUrl: e.currentTarget.value })}
          />
          <TextInput
            label="Product URL"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.currentTarget.value })}
          />
          <Textarea
            label="Description"
            autosize
            minRows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
          />
          <Button
            loading={addProduct.isPending}
            disabled={!form.name.trim() || !/^\d+(\.\d{1,2})?$/.test(form.price)}
            onClick={() =>
              addProduct.mutate({
                name: form.name.trim(),
                price: form.price,
                currency: form.currency || 'USD',
                availability: form.availability,
                imageUrl: form.imageUrl.trim() || undefined,
                url: form.url.trim() || undefined,
                description: form.description.trim() || undefined,
              })
            }
          >
            Add product
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}

export const Route = createFileRoute('/vendor/catalog')({ component: CatalogPage });
